import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TFunction } from "i18next";
import { FaEllipsisV, FaPlus } from "react-icons/fa";
import U2000ComboBox from "../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputPercentage from "../../../../u2000Core/components/formControls/input/U2000InputPercentage";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import { NS } from "../../../U3110";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import {
  U3110_APPLICATION_MODES,
  createEmptyComponentRow,
  minSortOrderAmongComponents,
  resequenceTaxRateComponents,
  type TsTaxRateComponentApplicationMode,
  type U3110TaxRateFormRow,
} from "../../../U3110Service";

interface U3110TaxRateComponentsSectionProps {
  formStateHandlers: U2000FormStateHandlers<U3110TaxRateFormRow>;
  t: TFunction;
}

function nextSortOrder(components: U3110TaxRateFormRow["taxRateComponents"]): number {
  if (components.length === 0) return 1;
  return Math.max(...components.map((c) => c.sortOrder)) + 1;
}

type ApplicationModeOption = Record<string, unknown> & {
  id: TsTaxRateComponentApplicationMode;
  description: string;
};

/**
 * Hauteur uniforme des &lt;input&gt; dans le tableau (aligné sur U3129 Honoraires, 46px).
 * Les composants U2000 ont des py différents (ex. py-3) : on impose h + py avec !.
 * Les boutons ne sont pas ciblés ici (évite d’étirer « Retirer ») : voir COMBO_TRIGGER_HEIGHT sur le combo uniquement.
 */
const COMPONENTS_TABLE_INPUT_HEIGHT =
  "[&_input]:box-border! [&_input]:h-[46px]! [&_input]:min-h-[46px]! [&_input]:max-h-[46px]! [&_input]:py-2.5! [&_input]:leading-snug!";

/** Bouton déclencheur du U2000ComboBox uniquement (pas les autres &lt;button&gt; du tableau). */
const COMBO_TRIGGER_HEIGHT = "[&_button]:box-border! [&_button]:h-[46px]! [&_button]:min-h-[46px]! [&_button]:max-h-[46px]! [&_button]:py-2.5! [&_button]:leading-snug!";

/** Bloc lecture seule : même hauteur que les champs éditables */
const readonlyCellClass =
  "flex h-[46px] min-h-[46px] w-full min-w-0 items-center rounded-lg border border-gray-200 bg-muted px-3 text-sm text-gray-700 cursor-not-allowed";

/** Alignement vertical des cellules sur la ligne */
const cellWrap = "px-2 py-2 align-middle min-w-0";

/** Largeur min. du panneau menu (px) — aligné sur min-w-52 */
const COMPONENT_ACTIONS_MENU_MIN_WIDTH = 208;

/** z-index au-dessus des en-têtes collants / conteneurs scrollables */
const COMPONENT_ACTIONS_MENU_Z_BACKDROP = 10040;
const COMPONENT_ACTIONS_MENU_Z_PANEL = 10050;

/** Aligné sur U3129HonorariumTab (HonorariumRowEditor) : lecture vs édition + champs U2000 reliés par chemin pointé. */
function TaxRateComponentRow({
  index,
  formStateHandlers,
  readonly,
  t,
  onRemove,
  canRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  modeOptions,
  minSortOrder,
}: {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3110TaxRateFormRow>;
  readonly: boolean;
  t: TFunction;
  onRemove: () => void;
  canRemove: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  modeOptions: ApplicationModeOption[];
  minSortOrder: number;
}) {
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsTriggerRef = useRef<HTMLButtonElement>(null);
  const actionsPanelRef = useRef<HTMLDivElement>(null);
  const [actionsMenuPos, setActionsMenuPos] = useState({ top: 0, left: 0 });

  const closeActionsMenu = () => setActionsMenuOpen(false);

  const openActionsMenu = () => {
    const el = actionsTriggerRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      const margin = 8;
      const left = Math.min(
        Math.max(margin, r.right - COMPONENT_ACTIONS_MENU_MIN_WIDTH),
        window.innerWidth - COMPONENT_ACTIONS_MENU_MIN_WIDTH - margin,
      );
      setActionsMenuPos({ top: r.bottom + 4, left });
    }
    setActionsMenuOpen(true);
  };

  const toggleActionsMenu = () => {
    if (actionsMenuOpen) closeActionsMenu();
    else openActionsMenu();
  };

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (actionsTriggerRef.current?.contains(target)) return;
      if (actionsPanelRef.current?.contains(target)) return;
      closeActionsMenu();
    };
    const onScroll = () => closeActionsMenu();
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [actionsMenuOpen]);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeActionsMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [actionsMenuOpen]);

  const baseField = `taxRateComponents.${index}`;
  const data = formStateHandlers.getData();
  const row = data?.taxRateComponents?.[index];
  const isBaseComponent =
    row !== undefined && Number.isFinite(row.sortOrder) && row.sortOrder === minSortOrder;

  const rateFieldWrap = "text-right [&_input]:text-right";

  if (readonly) {
    return (
      <tr className="border-b border-gray-100 hover:bg-gray-50/80">
        <td className={cellWrap}>
          <div className={readonlyCellClass}>{row?.sortOrder ?? "—"}</div>
        </td>
        <td className={cellWrap}>
          <div className={`${COMPONENTS_TABLE_INPUT_HEIGHT} ${rateFieldWrap}`}>
            <U2000InputPercentage
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.rate`}
              label=""
              readonly
              decimalDigits={3}
              allowNegative={false}
            />
          </div>
        </td>
        <td className={cellWrap}>
          <div className={readonlyCellClass}>{row?.labelFr?.trim() || "—"}</div>
        </td>
        <td className={cellWrap}>
          <div className={readonlyCellClass}>{row?.labelEn?.trim() || "—"}</div>
        </td>
        <td className={`min-w-[200px] ${cellWrap}`}>
          <div className={readonlyCellClass}>
            {isBaseComponent
              ? t("applicationMode.base", { ns: NS })
              : row?.applicationMode
                ? t(`applicationMode.${row.applicationMode}`, { ns: NS })
                : "—"}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className={cellWrap}>
        <div className={readonlyCellClass}>{row?.sortOrder ?? index + 1}</div>
      </td>
      <td className={cellWrap}>
        <div className={`${COMPONENTS_TABLE_INPUT_HEIGHT} ${rateFieldWrap}`}>
          <U2000InputPercentage
            formStateHandlers={formStateHandlers}
            fieldName={`${baseField}.rate`}
            label=""
            readonly={false}
            decimalDigits={3}
            allowNegative={false}
          />
        </div>
      </td>
      <td className={cellWrap}>
        <U2000InputText
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.labelFr`}
          label={t("lblCompLabelFr", { ns: NS })}
          hideLabel
        />
      </td>
      <td className={cellWrap}>
        <U2000InputText
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.labelEn`}
          label={t("lblCompLabelEn", { ns: NS })}
          hideLabel
        />
      </td>
      <td className={`min-w-[220px] ${cellWrap}`}>
        {isBaseComponent ? (
          <div className={readonlyCellClass}>{t("applicationMode.base", { ns: NS })}</div>
        ) : (
          <div className={`min-w-0 ${COMBO_TRIGGER_HEIGHT}`}>
            <U2000ComboBox<ApplicationModeOption>
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.applicationMode`}
              label=""
              data={modeOptions}
              keyField="id"
              descField="description"
              getValue={(item) => item.id}
              findItemByValue={(value, data) => data.find((d) => d.id === value) ?? null}
              formatLabel={(item) => String(item.description)}
            />
          </div>
        )}
      </td>
      <td className="w-14 min-w-14 px-1 py-2 align-middle">
        <button
          ref={actionsTriggerRef}
          type="button"
          aria-expanded={actionsMenuOpen}
          aria-haspopup="menu"
          className="flex h-[46px] w-full cursor-pointer items-center justify-center rounded-lg text-gray-500 outline-hidden ring-blue-500/30 hover:bg-gray-100 hover:text-gray-800 focus-visible:ring-2"
          aria-label={t("lblCompRowMenuTrigger", { ns: NS })}
          onClick={(e) => {
            e.stopPropagation();
            toggleActionsMenu();
          }}
        >
          <FaEllipsisV className="h-4 w-4" aria-hidden />
        </button>
        {actionsMenuOpen &&
          createPortal(
            <>
              <div
                className="fixed inset-0 bg-transparent"
                style={{ zIndex: COMPONENT_ACTIONS_MENU_Z_BACKDROP }}
                aria-hidden
                onClick={closeActionsMenu}
              />
              <div
                ref={actionsPanelRef}
                role="menu"
                className="fixed min-w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
                style={{
                  top: actionsMenuPos.top,
                  left: actionsMenuPos.left,
                  zIndex: COMPONENT_ACTIONS_MENU_Z_PANEL,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeActionsMenu();
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canMoveUp}
                  className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    onMoveUp();
                    closeActionsMenu();
                  }}
                >
                  {t("btnMoveComponentUp", { ns: NS })}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canMoveDown}
                  className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    onMoveDown();
                    closeActionsMenu();
                  }}
                >
                  {t("btnMoveComponentDown", { ns: NS })}
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canRemove}
                  className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    onRemove();
                    closeActionsMenu();
                  }}
                >
                  {t("btnRemoveRow", { ns: NS })}
                </button>
              </div>
            </>,
            document.body,
          )}
      </td>
    </tr>
  );
}

const U3110TaxRateComponentsSection: React.FC<U3110TaxRateComponentsSectionProps> = ({ formStateHandlers, t }) => {
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const components = data?.taxRateComponents ?? [];
  const minSortOrder = components.length > 0 ? minSortOrderAmongComponents(components) : 1;

  const modeOptions = useMemo(
    (): ApplicationModeOption[] =>
      U3110_APPLICATION_MODES.map((m) => ({
        id: m,
        description: t(`applicationMode.${m}`, { ns: NS }),
      })),
    [t],
  );

  const setComponents = (next: U3110TaxRateFormRow["taxRateComponents"]) => {
    const current = formStateHandlers.getData();
    if (!current) return;
    formStateHandlers.setData({ ...current, taxRateComponents: next });
  };

  const handleAdd = () => {
    setComponents([...components, createEmptyComponentRow(nextSortOrder(components), false)]);
  };

  const handleRemove = (index: number) => {
    if (components.length <= 1) return;
    const filtered = components.filter((_, i) => i !== index);
    setComponents(resequenceTaxRateComponents(filtered));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const j = index + direction;
    if (j < 0 || j >= components.length) return;
    const copy = [...components];
    [copy[index], copy[j]] = [copy[j], copy[index]];
    setComponents(resequenceTaxRateComponents(copy));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <U2000Label htmlFor="" label={t("lblSectionComponents", { ns: NS })} required />
        {!readonly && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-sm"
            onClick={handleAdd}
          >
            <FaPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("btnAddComponent", { ns: NS })}
          </button>
        )}
      </div>

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- InvalidFeedback attend handlers génériques larges */}
      <U2000InvalidFeedback formStateHandlers={formStateHandlers as any} fieldName="taxRateComponents" />

      <div className={`overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 ${COMPONENTS_TABLE_INPUT_HEIGHT}`}>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="sticky top-0 bg-muted/80">
            <tr>
              <th className="px-2 py-2 text-left font-medium">{t("lblCompSortOrder", { ns: NS })}</th>
              <th className="px-2 py-2 text-left font-medium">{t("lblCompRate", { ns: NS })}</th>
              <th className="px-2 py-2 text-left font-medium">{t("lblCompLabelFr", { ns: NS })}</th>
              <th className="px-2 py-2 text-left font-medium">{t("lblCompLabelEn", { ns: NS })}</th>
              <th className="px-2 py-2 text-left font-medium">{t("lblCompApplicationMode", { ns: NS })}</th>
              {!readonly && (
                <th className="w-14 min-w-14 px-1 py-2 text-center font-medium">
                  <span className="sr-only">{t("lblCompRowActions", { ns: NS })}</span>
                  <span className="text-xs font-bold text-gray-400 select-none" aria-hidden>
                    ···
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {components.map((row, index) => (
              <TaxRateComponentRow
                key={`${row.id ?? "new"}-${index}`}
                index={index}
                formStateHandlers={formStateHandlers}
                readonly={readonly}
                t={t}
                onRemove={() => handleRemove(index)}
                canRemove={components.length > 1}
                onMoveUp={() => handleMove(index, -1)}
                onMoveDown={() => handleMove(index, 1)}
                canMoveUp={index > 0}
                canMoveDown={index < components.length - 1}
                modeOptions={modeOptions}
                minSortOrder={minSortOrder}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default U3110TaxRateComponentsSection;
